/**
 * Divergence for computed rows (AP-731 §3) — a derived state, never
 * stored or served (R12). Diverged = the row holds a value that is not what its
 * formula produces, including when the formula has no result at all. An empty
 * computed row is never diverged. Reads only; writes nothing (R12).
 */

import { evaluate } from './formulaParser';

export type DivergenceState = 'converged' | 'diverged' | null;

export interface DivergenceResult {
  state: DivergenceState;
  /** Formula result at full precision; null when unevaluable. Shown only in the
   *  converge affordance (§4). */
  calculated: number | null;
  /** Operand keys that would not resolve — named in the hover text (R9). */
  missing: string[];
}

const CONVERGED: DivergenceResult = {
  state: 'converged',
  calculated: null,
  missing: [],
};

/** Warned once each, so one library defect doesn't spam every keystroke. */
const warnedBadFormulas = new Set<string>();

/** Round for comparison only (R11); operands stay full precision (R8). */
function atDisplayPrecision(value: number, precision: number): number {
  return Number(value.toFixed(precision));
}

/** `value` is the row's resolved value (manual→AI→SR); an operand absent from
 *  `valueBySlug` is one the reader cannot see, which diverges the row (R9). */
export function deriveDivergence(
  formula: string | null | undefined,
  value: number | null,
  valueBySlug: Map<string, number>,
  precision: number
): DivergenceResult {
  // Nothing to evaluate, so nothing to disagree with.
  if (!formula) return CONVERGED;

  let result;
  try {
    result = evaluate(formula, (key) => valueBySlug.get(key));
  } catch (err) {
    // One malformed formula must not take the whole worksheet down.
    if (!warnedBadFormulas.has(formula)) {
      warnedBadFormulas.add(formula);
      // eslint-disable-next-line no-console
      console.warn(`[AP-731] Malformed formula: ${formula}`, err);
    }
    return value === null
      ? CONVERGED
      : { state: 'diverged', calculated: null, missing: [] };
  }

  // No result: a valued row diverges and keeps its value (R9), an empty one
  // stays quiet (R11).
  if ('missing' in result) {
    return {
      state: value === null ? 'converged' : 'diverged',
      calculated: null,
      missing: result.missing,
    };
  }

  if (value === null) {
    return { state: 'converged', calculated: result.value, missing: [] };
  }

  const agrees =
    atDisplayPrecision(value, precision) ===
    atDisplayPrecision(result.value, precision);

  return {
    state: agrees ? 'converged' : 'diverged',
    calculated: result.value,
    missing: [],
  };
}
